import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { portalApi } from '../api/portalApi';

// Async thunk to fetch and evaluate long lead criteria for a specific ProjectID
export const fetchLongLead = createAsyncThunk(
  'longLead/fetchLongLead',
  async (projectID, { dispatch, rejectWithValue }) => {
    try {
      if (!projectID) {
        throw new Error('ProjectID is required');
      }

      // Use RTK Query to fetch data from all three endpoints in parallel
      const [priceBreakdownResult, pumpDetailsResult, soldToShipToResult] = await Promise.all([
        dispatch(
          portalApi.endpoints.getPriceBreakdown.initiate(projectID, { forceRefetch: true })
        ),
        dispatch(
          portalApi.endpoints.getPumpDetails.initiate(projectID, { forceRefetch: true })
        ),
        dispatch(
          portalApi.endpoints.getSoldToShipTo.initiate(projectID, { forceRefetch: true })
        ),
      ]);

      const priceBreakdownData = priceBreakdownResult.data || [];
      const pumpDetailsData = pumpDetailsResult.data || [];
      const soldToShipToData = soldToShipToResult.data || [];

      let highSellPrice = false;
      let highPumpHP = false;
      let internationalShip = false;

      if (Array.isArray(priceBreakdownData)) {
        priceBreakdownData.forEach(item => {
          if (item.SellPriceAmount && item.SellPriceAmount > 500000) {
            highSellPrice = true;
          }
        });
      }

      if (Array.isArray(pumpDetailsData)) {
        pumpDetailsData.forEach(pump => {
          if (pump.PumpHP && pump.PumpHP > 125) {
            highPumpHP = true;
          }
        });
      }

      if (Array.isArray(soldToShipToData)) {
        soldToShipToData.forEach(item => {
          if (item.InternationalShip === true) {
            internationalShip = true;
          }
        });
      }

      const criteriaMetFlag = highSellPrice || highPumpHP || internationalShip;

      return {
        projectID,
        criteriaMetFlag,
        details: { highSellPrice, highPumpHP, internationalShip },
        rawData: {
          priceBreakdown: priceBreakdownData,
          pumpDetails: pumpDetailsData,
          soldToShipTo: soldToShipToData,
        },
      };
    } catch (error) {
      return rejectWithValue({
        message: error.message || 'Failed to fetch long lead criteria',
        status: error.response?.status,
      });
    }
  }
);

// Reducer to manually trigger fetch (as requested)
export const triggerLongLeadFetch = createAsyncThunk(
  'longLead/triggerFetch',
  async (projectID, { dispatch }) => {
    return await dispatch(fetchLongLead(projectID));
  }
);

const longLeadSlice = createSlice({
  name: 'longLead',
  initialState: {
    data: {},
    loading: false,
    error: null,
    lastFetch: null,
    currentProjectID: null
  },
  reducers: {
    clearLongLead: (state) => {
      state.data = {};
      state.loading = false;
      state.error = null;
      state.lastFetch = null;
      state.currentProjectID = null;
    },
    resetError: (state) => {
      state.error = null;
    },
    setCurrentProjectID: (state, action) => {
      state.currentProjectID = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchLongLead.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.currentProjectID = action.meta.arg;
      })
      .addCase(fetchLongLead.fulfilled, (state, action) => {
        state.loading = false;
        state.data[action.payload.projectID] = action.payload;
        state.lastFetch = new Date().toISOString();
      })
      .addCase(fetchLongLead.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(triggerLongLeadFetch.pending, (state, action) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(triggerLongLeadFetch.fulfilled, (state, action) => {
        state.loading = false;
        // Data is already handled by the fetchLongLead case
      })
      .addCase(triggerLongLeadFetch.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearLongLead, resetError, setCurrentProjectID } = longLeadSlice.actions;

// Selectors
export const selectLongLeadData = (state) => state.longLead.data;
export const selectLongLeadLoading = (state) => state.longLead.loading;
export const selectLongLeadError = (state) => state.longLead.error;
export const selectLongLeadByProjectID = (state, projectID) => state.longLead.data[projectID];
export const selectCurrentProjectID = (state) => state.longLead.currentProjectID;

// Helper selector to get the criteria met flag for a specific project
export const selectCriteriaMetFlag = (state, projectID) => {
  const projectData = state.longLead.data[projectID];
  return projectData ? projectData.criteriaMetFlag : false;
};

export default longLeadSlice.reducer;